/* oxlint-disable grafit-oxlint/no-deprecated-typography, grafit-oxlint/no-tailwind-font-classes -- Typography 2 migration */
import { Button } from "@grafit/design-system/components/molecules/button";
import { Card, CardContent } from "@grafit/design-system/components/molecules/card";
import { toast } from "@grafit/design-system/components/molecules/toast";
import { useLocalStorage } from "@grafit/design-system/hooks/use-local-storage";
import { AUTH_METHOD_PREFERRED_EMAIL_KEY, SignInPage } from "@grafit/frontend-auth/sign-in-page";
import { signInWithSSO } from "@grafit/frontend-auth/sso-sign-in";
import { assertOnline } from "@grafit/frontend-auth/utils/assert-online";
import { AUTH_METHOD_SSO_PROVIDER_ID_KEY, clearActiveSSOAttempt } from "@grafit/frontend-auth/utils/sso-state";
import { useTranslation } from "@grafit/i18n";
import { UnexpectedError } from "@grafit/shared/domain/error/errors";
import { EVENTS, trackEvent } from "@grafit/shared/observability/analytics";
import { alertDevs, log } from "@grafit/shared/observability/sentry-front";
import { zodValidator } from "@tanstack/zod-adapter";
import { useEffect, useState } from "react";
import { z } from "zod";
import { useSearch } from "../scaffolding/router-api";
import { getSSOErrorMessageKey, getSSOErrorReference, shouldAlertDevsForSSOError } from "./sso-error-helpers";

export const ssoErrorlParamsSchemaValidator = zodValidator(
  z.object({
    error: z.string().optional(),
    error_description: z.string().optional(),
  }),
);

export const SSOErrorPage = () => {
  const { t } = useTranslation();
  const { error, error_description } = useSearch({ from: "/_no_auth/sso-error/$" });
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [ssoProviderId] = useLocalStorage(
    AUTH_METHOD_SSO_PROVIDER_ID_KEY,
    null,
    z.union([z.string(), z.literal(false), z.null()]),
  );
  const [userEmail] = useLocalStorage(AUTH_METHOD_PREFERRED_EMAIL_KEY, "", z.string());

  useEffect(() => {
    log("SSO error page viewed", "warn", {
      email: userEmail,
      error,
      error_description,
      sso_provider_id: ssoProviderId,
    });
    clearActiveSSOAttempt();

    if (!shouldAlertDevsForSSOError(error)) {
      return;
    }
    alertDevs({ error: new UnexpectedError("SSO Error"), context: { error, error_description, email: userEmail } });
  }, [error, error_description, ssoProviderId, userEmail]);

  const handleRetry = (): void => {
    if (!ssoProviderId) {
      return;
    }
    if (!assertOnline(t)) {
      return;
    }

    const newRetryCount = retryCount + 1;
    setRetryCount(newRetryCount);

    trackEvent(EVENTS.SSO_RETRY_CLICKED, {
      error,
      sso_provider_id: ssoProviderId,
      retry_attempt_number: newRetryCount,
    });

    setIsRetrying(true);
    void signInWithSSO({ providerId: ssoProviderId, callbackPath: "/", userEmail })
      .catch(() => {
        toast.error(t((r) => r["auth.sign-in.error.sso-unavailable"]));
      })
      .finally(() => {
        setIsRetrying(false);
      });
  };

  const errorMessage = t((r) => r[getSSOErrorMessageKey({ error, providerId: ssoProviderId })]);
  const errorReference = getSSOErrorReference({ error, errorDescription: error_description });
  return (
    <div className="mt-20 flex h-[100vh] flex-col">
      <div className="mx-auto -mb-20 w-lg">
        <Card className="relative mb-4 border-red-200 bg-red-50">
          <CardContent className="flex flex-col gap-2 px-4 py-2 text-center">
            <p className="text-red-900 text-sm">{errorMessage}</p>
            <p className="text-red-900 text-sm">{t((r) => r["sso.error.try-other-method"])}</p>
            {errorReference && (
              <p className="typo-body-xs break-all text-font-disabled">
                {t((r) => r["sso.error.technical-reference"])} {errorReference}
              </p>
            )}
          </CardContent>
        </Card>
        {ssoProviderId && (
          <Button
            variant={{ kind: "accent", renderMode: "fill", size: "m" }}
            className="mb-4 w-full"
            onClick={handleRetry}
            isPending={isRetrying}
          >
            {t((r) => r["sso.error.retry-button"])}
          </Button>
        )}
      </div>
      <SignInPage ssoError={true} />
    </div>
  );
};
